import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import {
  useCollegeDetails,
  useCoordinatorProfile,
  useCollegeClaims,
  profileKeys,
} from '@/api/queries/profile.queries';
import { useNotifications, notificationKeys } from '@/api/queries/notifications.queries';
import { useAuth } from './AuthProvider';
import { authService } from '@/services/auth.service';
import { storageService, STORAGE_KEYS } from '@/services/storage.service';
import type { CollegeProfile, CoordinatorProfile, CollegeClaims } from '@/types/auth.types';
import type { Notification } from '@/features/notifications/types';

// Routes where notifications should NOT be fetched
const AUTH_ROUTES = [
  '/sign-in',
  '/register',
  '/verification-code',
  '/reset-password',
  '/set-password',
  '/college-details-verification',
  '/college-verification',
  '/college-companies-details',
];

interface AppDataContextType {
  // College Profile
  collegeProfile: CollegeProfile | undefined;
  isCollegeProfileLoading: boolean;
  refetchCollegeProfile: () => void;

  // Coordinator Profile
  coordinatorProfile: CoordinatorProfile | undefined;
  isCoordinatorProfileLoading: boolean;
  refetchCoordinatorProfile: () => void;

  // Claims
  claims: CollegeClaims | undefined;
  isClaimsLoading: boolean;
  hasClaim: (claimName: string) => boolean;
  refetchClaims: () => void;

  // Notifications
  notifications: Notification[];
  notificationCount: number;
  unreadNotificationCount: number;
  isNotificationsLoading: boolean;
  refetchNotifications: () => void;
  markNotificationsAsRead: () => void;

  // Combined
  isLoading: boolean;
  refetchAll: () => void;
}

const AppDataContext = createContext<AppDataContextType | null>(null);

interface AppDataProviderProps {
  children: React.ReactNode;
}

export const AppDataProvider: React.FC<AppDataProviderProps> = ({ children }) => {
  const { isAuthenticated, setCollegeProfile, setCoordinatorProfile } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();

  // Check if current route is an auth/onboarding route
  const isAuthRoute = AUTH_ROUTES.some((route) => location.pathname.startsWith(route));

  // Check if college status is approved/inreview (not pending)
  const status = authService.getStatus();
  const isApprovedOrInReview = status === 'approved' || status === 'inreview';
  const isPending = status === 'pending';

  // Should fetch profile data only when authenticated, not on auth routes, and status is not pending
  // When status is pending, user is in onboarding flow and these APIs should not be called
  const shouldFetchProfileData = isAuthenticated && !isAuthRoute && !isPending;

  // Should fetch notifications only when authenticated, not on auth routes, and status is approved/inreview
  const shouldFetchNotifications = isAuthenticated && !isAuthRoute && isApprovedOrInReview;

  // Fetch college details - skip on auth/onboarding routes
  const {
    data: collegeProfile,
    isLoading: isCollegeProfileLoading,
    refetch: refetchCollegeProfile,
  } = useCollegeDetails(shouldFetchProfileData);

  // Fetch coordinator profile - skip on auth/onboarding routes
  const {
    data: coordinatorProfile,
    isLoading: isCoordinatorProfileLoading,
    refetch: refetchCoordinatorProfile,
  } = useCoordinatorProfile(shouldFetchProfileData);

  // Fetch claims - skip on auth/onboarding routes
  const {
    data: claims,
    isLoading: isClaimsLoading,
    refetch: refetchClaims,
  } = useCollegeClaims(shouldFetchProfileData);

  // Fetch notifications - only when not on auth routes and status is approved/inreview
  const {
    data: notificationsData,
    isLoading: isNotificationsLoading,
    refetch: refetchNotifications,
  } = useNotifications(1, 100, shouldFetchNotifications);

  const notifications = notificationsData?.notificationsModelList || [];
  const notificationCount = notifications.length;

  // Track last seen notification count for unread badge
  const [lastSeenCount, setLastSeenCount] = useState<number>(() => {
    const stored = storageService.getString(STORAGE_KEYS.NOTIFICATION_COUNT);
    return stored ? parseInt(stored, 10) : 0;
  });

  // Calculate unread count (new notifications since last viewed)
  const unreadNotificationCount = Math.max(0, notificationCount - lastSeenCount);

  // Mark all notifications as read (called when user views all notifications)
  const markNotificationsAsRead = useCallback(() => {
    setLastSeenCount(notificationCount);
    storageService.setString(STORAGE_KEYS.NOTIFICATION_COUNT, notificationCount.toString());
  }, [notificationCount]);

  // Sync profile data with AuthProvider for backward compatibility
  useEffect(() => {
    if (collegeProfile) {
      setCollegeProfile(collegeProfile);
    }
  }, [collegeProfile, setCollegeProfile]);

  useEffect(() => {
    if (coordinatorProfile) {
      setCoordinatorProfile(coordinatorProfile);
    }
  }, [coordinatorProfile, setCoordinatorProfile]);

  // Check if user has a specific claim
  const hasClaim = useCallback(
    (claimName: string): boolean => {
      if (!claims?.claims) return false;
      const claim = claims.claims.find((c) => c.name === claimName);
      return claim?.value || false;
    },
    [claims]
  );

  // Refetch all data
  const refetchAll = useCallback(() => {
    refetchCollegeProfile();
    refetchCoordinatorProfile();
    refetchClaims();
    refetchNotifications();
  }, [refetchCollegeProfile, refetchCoordinatorProfile, refetchClaims, refetchNotifications]);

  // Invalidate all queries on logout
  useEffect(() => {
    if (!isAuthenticated) {
      queryClient.removeQueries({ queryKey: profileKeys.all });
      queryClient.removeQueries({ queryKey: notificationKeys.all });
    }
  }, [isAuthenticated, queryClient]);

  // Set up polling for notifications (every 30 seconds) - only when shouldFetchNotifications is true
  useEffect(() => {
    if (!shouldFetchNotifications) return;

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [shouldFetchNotifications, queryClient]);

  const value: AppDataContextType = {
    collegeProfile,
    isCollegeProfileLoading,
    refetchCollegeProfile: () => refetchCollegeProfile(),

    coordinatorProfile,
    isCoordinatorProfileLoading,
    refetchCoordinatorProfile: () => refetchCoordinatorProfile(),

    claims,
    isClaimsLoading,
    hasClaim,
    refetchClaims: () => refetchClaims(),

    notifications,
    notificationCount,
    unreadNotificationCount,
    isNotificationsLoading,
    refetchNotifications: () => refetchNotifications(),
    markNotificationsAsRead,

    isLoading: isCollegeProfileLoading || isCoordinatorProfileLoading || isClaimsLoading,
    refetchAll,
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

// Custom hook to use app data
export const useAppData = (): AppDataContextType => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

export default AppDataProvider;
